import { useState, useEffect, useRef, ImgHTMLAttributes } from 'react';

interface LazyImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
    src: string;
    fallback?: React.ReactNode;
    /** Root margin for IntersectionObserver (default: '100px' for preloading) */
    rootMargin?: string;
}

/**
 * LazyImage component with IntersectionObserver fallback for iOS PWA compatibility.
 *
 * iOS Safari in PWA standalone mode has inconsistent behavior with native `loading="lazy"`.
 * This component uses IntersectionObserver which works reliably across all browsers and modes.
 *
 * Features:
 * - IntersectionObserver-based lazy loading (more reliable than native on iOS PWA)
 * - Preloads images 100px before they enter viewport
 * - Smooth fade-in animation on load
 * - Error handling with optional fallback
 * - Supports all standard img attributes
 */
export function LazyImage({
    src,
    fallback,
    rootMargin = '100px',
    className = '',
    onError,
    alt,
    ...props
}: LazyImageProps) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const [hasError, setHasError] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
        const element = imgRef.current;
        if (!element) return;

        // Check if IntersectionObserver is supported
        if (!('IntersectionObserver' in window)) {
            // Fallback: load immediately if IntersectionObserver not supported
            setIsInView(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        observer.unobserve(element);
                    }
                });
            },
            {
                rootMargin,
                threshold: 0,
            }
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [rootMargin]);

    // Reset state when src changes
    useEffect(() => {
        setIsLoaded(false);
        setHasError(false);
        setIsInView(false);
    }, [src]);

    const handleLoad = () => {
        setIsLoaded(true);
    };

    const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setHasError(true);
        onError?.(e);
    };

    if (hasError && fallback) {
        return <>{fallback}</>;
    }

    return (
        <img
            ref={imgRef}
            src={isInView ? src : undefined}
            alt={alt}
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
            className={`transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
            {...props}
        />
    );
}
